import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/RoutesTranslated';

import { useLocation, Link, useNavigate, Location, type NavigateFunction } from 'react-router-dom';

import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import { Container, Row, Col, Button, Form, Modal } from 'react-bootstrap';
import '@/ui/components/forms/form.css';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { changeLoginState, changeRecoveryState, changeRegisterState, setFromBuyButton } from '@/ui/store/slices/showModalsSlice';

import UserController from '@/controllers/UserController';

import { ILoginData } from '@/domain/interfaces/IUser';
import { IUserResponse } from '@/domain/interfaces/ILoginResponse';
import { translate } from '@/domain/utils/translate';
import BackIcon from '@/ui/assets/BackIcon';

import AccessByOthers from '@/ui/components/forms/AcessByOthers';
import InputForm from '@/ui/components/forms/InputForm';
import TextAndLink from '@/ui/components/TextAndLink';
import HeaderMobileUser from '@/ui/components/user/HeaderMobileUser';
import { getUrlFromCssVariable } from '@/domain/utils/CssToImage';


/** Componente formulario para login email y contraseña
 * @component
 */

interface PropsLoginForm {
  setShowLoginAlert?: (value: boolean) => void;
}

const LoginForm = React.memo(({
  setShowLoginAlert
}: PropsLoginForm): JSX.Element => {

  /** URL del logo de modales */
  const LOGO = useMemo(() => getUrlFromCssVariable('--img-logo-modals-white'), []);

  const { doLogin } = UserController();

  /*Trae la data del usuario registrado, incluyendo el tipo de usuario (kind)*/
  const { user } = useAppSelector((state) => state.userState);

  /**Estado de la modal de Login */
  const showLogin: boolean = useAppSelector((state) => state.showModals.showLogin);


  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = useMemo(() => width < maxLargeWidth, [width, maxLargeWidth]);

  /** Referencia al input email */
  const emailRef = useRef<HTMLInputElement>(null);

  /** Referencia al check mantenerme conectado */
  const sessionCheckRef = useRef<HTMLInputElement>(null);

  /** Referencia al input password */
  const passwordRef = useRef<HTMLInputElement>(null);

  /** Referencia al Formulario Login */
  const loginFormRef = useRef<HTMLFormElement>(null);

  /** Maneja un state para mostrar los inputs relacionados al email al cargar la web */
  const [inEmailInput, setInEmailInput] = useState<boolean>(true);
  /** Maneja un state para ocultar los inputs relacionados al password al cargar la web */
  const [inPasswordInput, setInPasswordInput] = useState<boolean>(false);

  /** Declaración inicial de los botones como deshabilitado */
  const [stateButton, setStateButton] = useState<string>('btn-primary-text-standard disabled');

  /** Declaración inicial un input en su estado normal */
  const [inputWithError, setInputWithError] = useState<boolean>(false);

  /** Maneja el state messageError para los inputs  */
  const [messageError, setMessageError] = useState<string>('');

  /** Maneja un state ocultar o mostrar el password digitado  */
  const [showPasswordText, setShowPasswordText] = useState<boolean>(false);

  /** Actualización de estado Redux */
  const dispatch = useAppDispatch();

  /** Evento que muestra y oculta el modal para login*/
  const handleShowLogin = useCallback((): void => {
    dispatch(changeLoginState());
  }, [dispatch]);

  /** Evento que muestra y oculta el modal para registro*/
  const handleShowRegister = useCallback((): void => {
    dispatch(changeRegisterState());
  }, [dispatch]);

  /** Evento que muestra y oculta el modal para recobrar contraseña */
  const handleShowPasswordRecovery = useCallback((): void => {
    dispatch(changeRecoveryState());
  }, [dispatch]);

  /** Muestra y Oculta la contraseña cada vez que se declara, solo si hay una contraseña escrita */
  const switchPasswordText = useCallback((): void => {
    setShowPasswordText(prevState => !prevState);
  }, []);

  /** Rutas URLs */
  const routesDirections = useMemo(() => getRoutesDirections(), []);

  const fromBuyButton = useAppSelector((state) => state.showModals.fromBuyButton);

  /** Enciende el input relacionado al email*/
  const onEmailInput = useCallback((): void => {
    setInEmailInput(true);
  }, []);

  const offPasswordInput = useCallback((): void => {
    setInPasswordInput(false);
  }, []);

  /** Habilita un botón para avanzar a la siguiente fase*/
  const onButton = useCallback((): void => {
    setStateButton('btn-primary-text-standard');
  }, []);

  /** Deshabilita un botón para bloquear el avance a la siguiente fase*/
  const offButton = useCallback((): void => {
    setStateButton('btn-primary-text-standard disabled');
  }, []);

  /** Hook para generar una navegación dentro de la aplicación.  */
  const navigate: NavigateFunction = useNavigate();

  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  /** Función para activar input,
   *  Oculta los mensajes,
   *  Oculta los iconos de error,
   *  Coloca el input como valido (formato inicial),
   *  Enciende el botón,
   */
  const validInput = useCallback((): void => {
    setMessageError('');
    setInputWithError(false);
    onButton();
  }, [onButton]);

  /** Función para etiquetar un input con errores
   *  Coloca los mensajes como error (en caso de existir),
   *  Muestra los iconos de error,
   *  Coloca el input como error,
   *  Apaga el botón,
   */
  const errorInput = useCallback((): void => {
    setInputWithError(true);
    offButton();
  }, [offButton]);

  /** Función para etiquetar un input vacío,
   *  Oculta los mensajes,
   *  Oculta los iconos de error,
   *  Coloca el input como valido (formato inicial),
   *  Apaga el botón,
   */
  const emptyInput = useCallback((): void => {
    setMessageError('');
    setInputWithError(false);
    offButton();
  }, [offButton]);

  /** Resetea el formulario de Registro,
   * Enciende el Input email y oculta el resto de Inputs,
   * Define el Input en su estado inicial,
   * Oculta iconos de error y componentes que no corresponden al input de email,
   * Borra todos los valores del form
   */
  const resetForm = useCallback((): void => {
    onEmailInput();
    offPasswordInput();
    emptyInput();
    setShowPasswordText(false);
    loginFormRef.current?.reset();
  }, [onEmailInput, offPasswordInput, emptyInput]);

  /** Resetea y Cierra el Formulario  */
  const closeForm = useCallback((): void => {
    dispatch(changeLoginState());
    resetForm();
  }, [dispatch, resetForm]);

  /** Función para hacer login y redirigir al usuario a la ruta correspondiente */
  const handleSubmit = useCallback(async (isALogin: boolean = true): Promise<IUserResponse> => {
    const keepLogin: boolean = sessionCheckRef.current?.checked ?? true;
    const userLogin: ILoginData = {
      email: emailRef.current?.value!,
      password: passwordRef.current?.value!
    };

    const doLoginResponse: IUserResponse = await doLogin(userLogin, isALogin, keepLogin);

    if (doLoginResponse.isError) {
      errorInput();
      setMessageError(doLoginResponse.message[0]);
    } else {
      // Forzar cierre del modal inmediatamente
      dispatch(changeLoginState());
      // También resetear el formulario
      resetForm();
    }

    return doLoginResponse;
  }, [doLogin, errorInput, dispatch, resetForm]);

  /** Manejador del evento onChange en los input email y password  */
  const handleInput = useCallback(async (): Promise<boolean> => {
    let error: boolean = false;

    const emailValue = emailRef.current?.value ?? '';
    const passwordValue = passwordRef.current?.value ?? '';

    const validation = await handleSubmit(false);
    const errorMessage = () => {
      errorInput();
      setMessageError(validation.message[0]);
      error = true;
    };

    if (!emailValue || !passwordValue) {
      errorMessage();
      error = true;
    }

    if (inEmailInput ? validation.inputError === 'email' : null) {
      errorMessage();
      error = true;
    } else if (inPasswordInput ? validation.inputError === 'password' : null) {
      errorMessage();
    } else {
      validInput();
    }
    return error;
  }, [handleSubmit, errorInput, validInput, inEmailInput, inPasswordInput]);

  /** Cambia el modal del login por el de registro  */
  const toSignUp = useCallback((): void => {
    handleShowRegister();
    closeForm();
  }, [handleShowRegister, closeForm]);

  /** Cambiar el modal a recovery password */
  const toRecoveryPassword = useCallback((): void => {
    handleShowPasswordRecovery();
    handleShowLogin();
  }, [handleShowPasswordRecovery, handleShowLogin]);

  /** Efecto que se ejecuta al cargar el componente y al cambiar el estado de la modal de login */
  useEffect(() => {
    if (user.isLogued && showLogin) {
      // Cierra correctamente el modal y resetea el formulario
      dispatch(changeLoginState());
      resetForm();

      // Muestra la alerta (si está definida)
      setShowLoginAlert?.(true);

      const isCartRoute = location.pathname === routesDirections.CART_ROUTE;
      const isAdmin = user.kind === 'ADMIN';

      if (!isCartRoute) {
        if (isAdmin && isMobile) {
          // Si es admin Y mobile -> home
          navigate(routesDirections.MAIN_ROUTE);
        } else if (isAdmin) {
          // Si es admin pero NO mobile -> inventario
          navigate(routesDirections.INVENTORY_ROUTE);
        } else if (fromBuyButton) {
          // No es admin -> mantén la lógica original
          dispatch(setFromBuyButton(false));
          navigate(routesDirections.CHECK_OUT_ROUTE);
        } else {
          navigate(routesDirections.MAIN_ROUTE);
        }
      }

      window.scrollTo(0, 0);
    }
  }, [user.isLogued, showLogin, dispatch, resetForm, navigate, location.pathname, routesDirections, fromBuyButton, setShowLoginAlert]);

  // Efecto adicional para cerrar el modal inmediatamente después de un inicio de sesión exitoso
  useEffect(() => {
    // Si el usuario está logueado y el modal está visible, cerrarlo inmediatamente
    if (user.isLogued && showLogin) {
      // Cerrar el modal directamente
      dispatch(changeLoginState());
    }
  }, [user.isLogued, showLogin, dispatch]);



  /** Constantes de texto para el formulario de login */
  const FORGOT_PASSWORD_TEXT = useMemo(() => translate('session.forgot_password_text'), []);
  const CONTINUE_TEXT = useMemo(() => translate('session.continue_text'), []);
  const LOGIN_TEXT = useMemo(() => translate('session.login_text'), []);

  /** Renderizado del componente */
  return (
    <Modal
      id='login-modal'
      show={showLogin}
      onHide={handleShowLogin}
      fullscreen={true}
      animation={false}
      backdrop="static"
    >
      {isMobile ? (
        <Modal.Header className='d-flex p-0 bg-ilis-modal'>
          <HeaderMobileUser
            handleIconClick={() => {
              dispatch(changeLoginState());
              resetForm();
            }}
            logoSrc={LOGO}
            iconClass='btn-icon-dark svg-primary-4'
            logoClass='logo-header-modal'
          />
        </Modal.Header>
      ) : null}
      <Modal.Body className='p-0'>
        <Container fluid>
          <Row id='login' className={isMobile ? 'd-flex align-items-end flex-column p-0 ' : 'min-vh-100 d-flex align-items-end flex-column p-0  '}>
            {!isMobile && (
              <Col xs={12} className='bg-ilis-modal min-vh-50'>
                <Col xs={12} className='align-self-center d-flex justify-content-around pb-5 '>
                  <Col lg={11} xs={12} className='text-start'>
                    <BackIcon
                      onClick={handleShowLogin}
                      classMain='btn-icon-dark my-5 svg-primary-4 no-pb-mt'
                    />
                  </Col>
                  <div>
                    <img
                      src={LOGO}
                      id='modal-desktop-logo'
                      alt='logo'
                      className='login-desktop-logo d-none d-sm-block position-absolute top-10 start-10 logo-full-modal'>
                    </img>
                  </div>
                </Col>
              </Col>
            )}

            <Col
              xs={12}
              sm={6}
              lg={4}
              className={
                isMobile
                  ? 'bg-primary-4 rounded-top-5  '
                  : `bg-primary-4 position-absolute top-50 start-50 translate-middle rounded-top-5 mt-5 ${!isMobile && 'shadow'}`
              }>
              <Row>
                <Col xs={12} className='pt-5 min-vh-25 d-flex align-items-center flex-column no-pb-mt '>
                  <Col xs={12} className='pb-4 mt-4 text-center no-pb-mt'>
                    <span className='h2 fw-bold text-secondary-1'>{LOGIN_TEXT}</span>
                  </Col>
                  <Col xs={10} lg={7}>
                    <Col xs={12}>
                      <Form
                        id='login-form'
                        ref={loginFormRef}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                        className='svg-tertiary-1 '>
                        <div>
                          <InputForm
                            className='mb-3 mt-4 no-pb-mt'
                            label={translate('session.email')}
                            classLabel='p-smaller text-secondary-2 '
                            id='input-login-email'
                            inputRef={emailRef}
                            type='email'
                            iconName='ico-error'
                            onChange={handleInput}
                            withErrors={inputWithError}
                            lastIconName={inputWithError ? 'ico-info' : ''}
                          />
                        </div>

                        <div>
                          <InputForm
                            label={translate('session.password_label')}
                            iconName={showPasswordText ? 'ico-show' : 'ico-hide'}
                            classLabel='p-smaller text-secondary-2 '
                            id='input-login-password'
                            inputRef={passwordRef}
                            type={showPasswordText ? 'text' : 'password'}
                            withErrors={inputWithError}
                            onChange={handleInput}
                            lastIconClicked={switchPasswordText}
                            lastIconClass={`last-input-icon ${showPasswordText ? '' : 'disabled'}`}
                            lastIconName={showPasswordText ? 'ico-show' : 'ico-hide'}
                          />
                        </div>
                        <Col className='my-4 text-danger p-regular'>
                          <span>{messageError}</span>
                        </Col>

                        <div className='d-grid gap-2 mt-4 no-pb-mt'>
                          <Button className={stateButton} type='submit'>
                            <span className='p-large '>{CONTINUE_TEXT}</span>
                          </Button>
                        </div>

                        <Col className='my-3 text-center'>
                          <Link to={'#'} className='text-tertiary-4 p-large fw-bold ' onClick={toRecoveryPassword}>
                            {FORGOT_PASSWORD_TEXT}
                          </Link>
                        </Col>

                        {/* {inEmailInput && (
                          <div>
                            <AccessByOthers />
                          </div>
                        )} */}
                        {/* <Col className='my-5 py-3'></Col> */}
                      </Form>
                    </Col>
                  </Col>
                </Col>
              </Row>
              <Container fluid className='p-0 mt-auto'>
                <Row>
                  <Col xs={12} className=' pt-4 pb-5 mb-4'>
                    <TextAndLink
                      text={translate('session.ask_new_user')}
                      linkText={translate('session.create_account_label')}
                      onClick={toSignUp}
                      classText='text-center'
                      separated={false}
                      classLinkButton='link-text-standard p-large fw-bold px-3'
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
});

export default LoginForm;
